import { ChevronDown, Download, FileText } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { FinancialReport } from '@/hooks/useReports';
import { track } from '@/lib/mixpanel';
import { cn } from '@/lib/utils';

function formatDateTime(dateString: string) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  return date.toLocaleString('ro-RO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

type ReportAttachment = {
  href: string;
  name: string;
  type: 'PDF' | 'ZIP';
};

function getMetadataStringArray(
  report: FinancialReport,
  keys: string[]
): string[] {
  for (const key of keys) {
    const value = report.raw_metadata?.[key];
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string');
    }
  }

  return [];
}

function inferFileType(value: string) {
  return value.toLowerCase().includes('.zip') ? 'ZIP' : 'PDF';
}

function normalizeFileName(value: string) {
  return value
    .replace(/\.(pdf|zip)$/i, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getFileNameFromUrl(href: string) {
  try {
    const pathname = new URL(href).pathname;
    const filename = decodeURIComponent(pathname.split('/').pop() ?? '');
    const normalized = normalizeFileName(filename);

    return /[A-Za-z0-9]/.test(normalized) ? normalized : null;
  } catch {
    return null;
  }
}

function humanizeFileSegment(value: string) {
  return value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function getAttachmentDisplayParts(name: string) {
  const normalizedName = name.trim();
  const machinePrefixMatch = normalizedName.match(
    /^([A-Z0-9]+)[ _-]+(\d{8,14})[ _-]+(.+)$/
  );

  if (machinePrefixMatch) {
    const [, , , documentName] = machinePrefixMatch;

    return {
      title: humanizeFileSegment(documentName),
    };
  }

  return {
    title: humanizeFileSegment(normalizedName),
  };
}

function getReportPdfLinks(report: FinancialReport): ReportAttachment[] {
  const metadataLabels = getMetadataStringArray(report, ['pdf_link_labels']);
  const metadataNames = getMetadataStringArray(report, [
    'file_names',
    'document_names',
    'attachment_names',
    'pdf_file_names',
  ]);
  const sourceLinks = report.pdf_urls.filter(Boolean);

  return sourceLinks.map((url, index) => ({
    href: url,
    name:
      normalizeFileName(metadataNames[index]?.trim() || '') ||
      (metadataLabels[index]?.trim() &&
      !/^\d+$/.test(metadataLabels[index].trim())
        ? normalizeFileName(metadataLabels[index].trim())
        : getFileNameFromUrl(url)) ||
      `Fișier ${index + 1}`,
    type: inferFileType(
      `${url} ${metadataNames[index] ?? ''} ${metadataLabels[index] ?? ''}`
    ),
  }));
}

type Props = {
  reports: FinancialReport[];
  isLoading: boolean;
  onLoadMore: () => void;
  hasMore: boolean;
  isFetchingNextPage: boolean;
};

function TableSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

export function ReportsTable({
  reports,
  isLoading,
  onLoadMore,
  hasMore,
  isFetchingNextPage,
}: Props) {
  const [expandedReportId, setExpandedReportId] = useState<number | null>(null);

  useEffect(() => {
    if (
      expandedReportId !== null &&
      !reports.some((report) => report.id === expandedReportId)
    ) {
      setExpandedReportId(null);
    }
  }, [expandedReportId, reports]);

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (reports.length === 0) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-12 text-center">
        <FileText className="mx-auto h-12 w-12 text-neutral-300" />
        <p className="mt-4 text-neutral-500">
          Nu au fost găsite raportări pentru filtrele selectate.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-neutral-200 bg-white">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-24">Simbol</TableHead>
            <TableHead>Titlu</TableHead>
            <TableHead className="w-32">Data</TableHead>
            <TableHead className="w-64">Tip document</TableHead>
            <TableHead className="w-16" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {reports.map((report) => {
            const attachments = getReportPdfLinks(report);
            const isExpanded = expandedReportId === report.id;
            const toggleExpanded = () => {
              const willExpand = expandedReportId !== report.id;
              if (willExpand) {
                track('Reports_ReportExpanded', {
                  reportId: report.id,
                  symbol: report.symbol,
                });
              }
              setExpandedReportId((currentId) =>
                currentId === report.id ? null : report.id
              );
            };

            return (
              <Fragment key={report.id}>
                <TableRow
                  className={cn(
                    'cursor-pointer hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
                    isExpanded && 'border-b-0 bg-neutral-50'
                  )}
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  aria-controls={`report-files-${report.id}`}
                  onClick={toggleExpanded}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleExpanded();
                    }
                  }}>
                  <TableCell>
                    <Badge variant="secondary" className="font-mono">
                      {report.symbol}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-2 text-sm text-neutral-800">
                      <FileText className="h-3.5 w-3.5 shrink-0" />
                      {report.title}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-neutral-500">
                    {formatDateTime(report.published_date)}
                  </TableCell>
                  <TableCell className="text-sm text-neutral-700">
                    {report.filing_category ?? '-'}
                  </TableCell>
                  <TableCell className="pr-4 text-right">
                    <span className="sr-only">
                      {isExpanded
                        ? 'Ascunde documentele atașate'
                        : 'Arată documentele atașate'}
                    </span>
                    <ChevronDown
                      aria-hidden="true"
                      className={cn(
                        'ml-auto h-4 w-4 shrink-0 text-neutral-500 transition-transform duration-200',
                        isExpanded && 'rotate-180'
                      )}
                    />
                  </TableCell>
                </TableRow>

                <TableRow className="border-0 hover:bg-transparent">
                  <TableCell colSpan={5} className="p-0">
                    <div
                      id={`report-files-${report.id}`}
                      aria-hidden={!isExpanded}
                      className={cn(
                        'grid transition-[grid-template-rows,opacity] duration-200 ease-out',
                        isExpanded
                          ? 'grid-rows-[1fr] opacity-100'
                          : 'pointer-events-none grid-rows-[0fr] opacity-0'
                      )}>
                      <div className="overflow-hidden">
                        <div className="border-b border-neutral-200 bg-neutral-50/80 px-3 py-4 sm:px-4">
                          <div className="rounded-2xl border border-neutral-200/80 bg-white/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] backdrop-blur-sm">
                            <div className="px-4 py-3">
                              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-500">
                                Documente atașate ({attachments.length})
                              </p>
                            </div>

                            <div className="divide-y divide-neutral-100">
                              {attachments.map((attachment, index) => {
                                const displayParts = getAttachmentDisplayParts(
                                  attachment.name
                                );

                                return (
                                  <a
                                    key={`${report.id}-${attachment.href}-${index}`}
                                    href={attachment.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    tabIndex={isExpanded ? undefined : -1}
                                    download
                                    onClick={() =>
                                      track('Reports_AttachmentDownloaded', {
                                        reportId: report.id,
                                        symbol: report.symbol,
                                        fileType: attachment.type,
                                      })
                                    }
                                    className="group flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30">
                                    <div
                                      className={cn(
                                        'flex h-6 min-w-6 items-center justify-center rounded-md border px-1.5 text-[8px] font-semibold uppercase tracking-[0.12em] transition-colors',
                                        attachment.type === 'ZIP'
                                          ? 'border-amber-200 bg-amber-50 text-amber-700'
                                          : 'border-red-200 bg-red-50 text-red-600'
                                      )}>
                                      {attachment.type}
                                    </div>

                                    <div className="min-w-0 flex-1">
                                      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                                        <p className="truncate text-sm font-medium text-neutral-900 transition-colors group-hover:text-primary">
                                          {displayParts.title}
                                        </p>
                                      </div>
                                    </div>

                                    <div className="flex shrink-0 items-center">
                                      <Download className="h-4 w-4 text-neutral-300 transition-colors group-hover:text-primary" />
                                    </div>
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>

      {hasMore && (
        <div className="border-t border-neutral-200 p-4 text-center">
          <Button
            variant="ghost"
            onClick={() => {
              track('Reports_LoadMoreClicked');
              onLoadMore();
            }}
            disabled={isFetchingNextPage}>
            {isFetchingNextPage ? 'Se încarcă...' : 'Încarcă mai multe'}
          </Button>
        </div>
      )}
    </div>
  );
}
